import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ActionCluster, DisclosurePanel, PipelineStrip, SectionHeader } from '../components/AppShellPrimitives';
import {
  createRuntimeScenarioEvalPack,
  createRuntimeScenarioReview,
  fetchRuntimeRiskSuggestionPreview,
  fetchRuntimeRiskSuggestionSendStatus,
  sendRuntimeRiskSuggestion,
} from '../data';

function humanize(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function statusLabel(status) {
  switch (status) {
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    case 'needs_more_evidence':
      return 'Needs more evidence';
    default:
      return 'Review needed';
  }
}

function defaultExpectedBehavior(candidate) {
  if (!candidate?.scenario_label) {
    return '';
  }
  return `The workflow should handle "${candidate.scenario_label}" without repeating the observed runtime pattern.`;
}

function normalizeMultiline(value) {
  return String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
    .join('\n');
}

function sendStatusLabel(status) {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'duplicate_accepted':
      return 'Already accepted';
    case 'send_pending':
      return 'Sending';
    case 'send_failed':
      return 'Send failed';
    case 'accepted_not_sent':
      return 'Not sent';
    default:
      return 'Not sent';
  }
}

function coverageStatusLabel(status) {
  switch (status) {
    case 'passing_eval':
      return 'Passing evaluation';
    case 'failing_eval':
      return 'Failing evaluation';
    case 'needs_labels':
      return 'Needs labels';
    case 'covered_by_tests':
      return 'Covered by tests';
    case 'reviewed_in_eval_tool':
      return 'Reviewed in eval tool';
    case 'dismissed_in_eval_tool':
      return 'Dismissed in eval tool';
    case 'stale':
      return 'Stale';
    case 'sent':
      return 'Sent';
    case 'accepted_not_sent':
      return 'Accepted, not sent';
    case 'needs_more_evidence':
      return 'Needs more evidence';
    case 'not_reviewed':
    default:
      return 'Not reviewed';
  }
}

function coverageChipClass(status) {
  if (status === 'passing_eval' || status === 'covered_by_tests') {
    return 'chip chip-success';
  }
  if (['failing_eval', 'needs_labels', 'stale', 'dismissed_in_eval_tool'].includes(status)) {
    return 'chip chip-warning';
  }
  return 'chip';
}

function sentCoverageStatusFromSend(send, existingCoverage) {
  if (!send || !['sent', 'duplicate_accepted'].includes(send.status)) {
    return existingCoverage || null;
  }
  return {
    ...(existingCoverage || {}),
    status: 'sent',
    label: 'Sent',
    summary: 'The runtime risk was sent to evaluation coverage.',
    reason: 'No compact Automated-System-Eval outcome summary has been imported yet.',
    runtime_scenario_review_id: send.runtime_scenario_review_id || existingCoverage?.runtime_scenario_review_id || null,
    runtime_risk_suggestion_send_id: send.id || existingCoverage?.runtime_risk_suggestion_send_id || null,
    external_suggestion_id: send.external_suggestion_id || existingCoverage?.external_suggestion_id || null,
    source_url: send.external_url || existingCoverage?.source_url || null,
  };
}

function mergeSuccessfulSendStatus(previousStatus, nextStatus) {
  const previousSend = previousStatus?.latest_send;
  const previousSucceeded = previousSend && ['sent', 'duplicate_accepted'].includes(previousSend.status);
  if (!previousSucceeded) {
    return nextStatus;
  }
  const nextSend = nextStatus?.latest_send;
  const nextSucceeded = nextSend && ['sent', 'duplicate_accepted'].includes(nextSend.status);
  if (nextSucceeded) {
    return nextStatus;
  }
  return {
    ...(nextStatus || previousStatus || {}),
    latest_send: previousSend,
    can_send: false,
    coverage_status: sentCoverageStatusFromSend(previousSend, nextStatus?.coverage_status || previousStatus?.coverage_status),
  };
}

function formatCoverageDate(value) {
  if (!value) {
    return null;
  }
  return new Date(value).toLocaleString();
}

export function RuntimeRiskCoverageStatusPanel({ coverage, compact = false }) {
  if (!coverage) {
    return null;
  }
  const executedAt = formatCoverageDate(coverage.latest_executed_at);
  const rowCounts = [
    ['Generated', coverage.generated_row_count || 0],
    ['Evaluated', coverage.evaluated_row_count || 0],
    ['Passed', coverage.passed_row_count || 0],
    ['Failed', coverage.failed_row_count || 0],
  ];
  if (compact) {
    return (
      <div className="eval-pack-guidance-block runtime-risk-coverage-status runtime-risk-coverage-status-compact" data-testid="runtime-risk-coverage-status">
        <div>
          <span className="summary-card-label">Coverage detail</span>
          <p>{coverage.reason || coverage.summary || coverageStatusLabel(coverage.status)}</p>
        </div>
        <div className="runtime-coverage-stat-strip" aria-label="Evaluation row counts">
          {rowCounts.map(([label, value]) => (
            <span className="runtime-coverage-stat" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </span>
          ))}
        </div>
        <div className="chip-row">
          {coverage.unclear_row_count ? <span className="chip chip-warning">Unclear {coverage.unclear_row_count}</span> : null}
          {coverage.unlabeled_row_count ? <span className="chip chip-warning">Unlabeled {coverage.unlabeled_row_count}</span> : null}
          {executedAt ? <span className="chip">Executed {executedAt}</span> : null}
          {coverage.external_evaluation_run_id ? <span className="chip">Run {coverage.external_evaluation_run_id}</span> : null}
        </div>
        {coverage.source_url ? (
          <a className="secondary-button action-link-button runtime-handoff-external-link" href={coverage.source_url} target="_blank" rel="noreferrer">
            Open evaluation report
          </a>
        ) : null}
      </div>
    );
  }
  return (
    <div className="eval-pack-guidance-block runtime-risk-coverage-status" data-testid="runtime-risk-coverage-status">
      <div className="workflow-mode-header">
        <div>
          <span className="summary-card-label">Evaluation coverage</span>
          <strong>{coverage.summary || coverageStatusLabel(coverage.status)}</strong>
          <p>{coverage.reason}</p>
        </div>
        <span className={coverageChipClass(coverage.status)}>{coverageStatusLabel(coverage.status)}</span>
      </div>
      <div className="runtime-coverage-stat-strip" aria-label="Evaluation row counts">
        {rowCounts.map(([label, value]) => (
          <span className="runtime-coverage-stat" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </span>
        ))}
      </div>
      <div className="chip-row">
        {coverage.unclear_row_count ? <span className="chip chip-warning">Unclear {coverage.unclear_row_count}</span> : null}
        {coverage.unlabeled_row_count ? <span className="chip chip-warning">Unlabeled {coverage.unlabeled_row_count}</span> : null}
        {executedAt ? <span className="chip">Executed {executedAt}</span> : null}
        {coverage.external_evaluation_run_id ? <span className="chip">Run {coverage.external_evaluation_run_id}</span> : null}
      </div>
      {coverage.source_url ? (
        <a className="secondary-button action-link-button runtime-handoff-external-link" href={coverage.source_url} target="_blank" rel="noreferrer">
          Open evaluation report
        </a>
      ) : null}
    </div>
  );
}

function RuntimeRiskSuggestionPreviewPanel({
  previewState,
  loading,
  error,
  onRefresh,
  sendStatus,
  sendStatusLoading,
  sendStatusError,
  sending,
  onSend,
}) {
  if (!previewState && !loading && !error) {
    return null;
  }
  const preview = previewState?.preview;
  const ready = previewState?.readiness_status === 'ready';
  const destination = sendStatus?.destination;
  const latestSend = sendStatus?.latest_send;
  const coverage = sendStatus?.coverage_status;
  const sendSucceeded = latestSend && ['sent', 'duplicate_accepted'].includes(latestSend.status);
  const displayedCoverage = sendSucceeded ? sentCoverageStatusFromSend(latestSend, coverage) : coverage;
  const sendFailed = latestSend?.status === 'send_failed';
  const canSend = ready && sendStatus?.can_send && !sendSucceeded;
  const handoffComplete = ready && sendSucceeded;
  const observedRuntimePattern = preview?.observed_runtime_pattern || null;
  const hiddenPreviewDetailCount =
    (preview?.evidence_gaps?.length || 0) +
    (preview?.source_finding_title || preview?.source_finding_id ? 1 : 0) +
    (observedRuntimePattern ? 1 : 0) +
    (preview?.expected_behavior_summary ? 1 : 0) +
    (preview?.suspected_risk_summary ? 1 : 0);
  return (
    <div className="runtime-handoff-preview" data-testid="runtime-handoff-preview">
      <div className="workflow-mode-header">
        <div>
          <span className="summary-card-label">Evaluation handoff preview</span>
          <strong>{handoffComplete ? 'Sent to evaluation coverage' : ready ? 'Ready for review' : 'Needs attention'}</strong>
          <p>
            {handoffComplete
              ? 'This accepted runtime risk has been handed off without sharing raw runtime content.'
              : 'This is the content Agentic Blue can prepare for evaluation coverage review without sharing raw runtime content.'}
          </p>
        </div>
        <span className={handoffComplete ? 'chip chip-success' : ready ? 'chip chip-emphasis' : 'chip chip-warning'}>
          {handoffComplete ? 'Sent' : ready ? 'Ready' : 'Blocked'}
        </span>
      </div>
      {loading ? <p className="overview-note">Preparing preview…</p> : null}
      {error ? <p className="overview-note">{error}</p> : null}
      {sendStatusLoading ? <p className="overview-note">Checking evaluation destination…</p> : null}
      {sendStatusError ? <p className="overview-note">{sendStatusError}</p> : null}
      {preview ? (
        <>
          <div className="chip-row runtime-handoff-privacy-row">
            <span className="chip">{preview.raw_content_excluded ? 'Raw runtime content excluded' : 'Raw content check unavailable'}</span>
            {preview.expectation_type ? <span className="chip">{humanize(preview.expectation_type)}</span> : null}
          </div>
          <DisclosurePanel className="runtime-handoff-secondary-details" title="Handoff details" summaryMeta={hiddenPreviewDetailCount ? `${hiddenPreviewDetailCount} items` : 'Optional'}>
            {preview.expected_behavior_summary || preview.suspected_risk_summary || observedRuntimePattern ? (
              <div className="runtime-handoff-detail-stack">
                {observedRuntimePattern ? (
                  <div>
                    <span className="summary-card-label">Observed runtime pattern</span>
                    <p>{observedRuntimePattern}</p>
                  </div>
                ) : null}
                {preview.expected_behavior_summary ? (
                  <div>
                    <span className="summary-card-label">Expected behavior</span>
                    <p>{preview.expected_behavior_summary}</p>
                  </div>
                ) : null}
                {preview.suspected_risk_summary ? (
                  <div>
                    <span className="summary-card-label">Risk note</span>
                    <p>{preview.suspected_risk_summary}</p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="runtime-compact-kv">
              <span>Source finding</span>
              <strong>{preview.source_finding_title || preview.source_finding_id || 'Unavailable'}</strong>
            </div>
            {preview.evidence_gaps?.length ? (
              <div>
                <span className="summary-card-label">Evidence gaps</span>
                <ul className="eval-pack-guidance-list">
                  {preview.evidence_gaps.map((gap) => (
                    <li key={gap}>{gap}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </DisclosurePanel>
        </>
      ) : null}
      {previewState?.blockers?.length ? (
        <div className="eval-pack-guidance-block runtime-handoff-blockers">
          <span className="summary-card-label">Blockers</span>
          <ul className="eval-pack-guidance-list">
            {previewState.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {previewState?.warnings?.length ? (
        <DisclosurePanel className="runtime-review-notes-disclosure" title="Review notes" summaryMeta={`${previewState.warnings.length} hidden`}>
          <ul className="eval-pack-guidance-list">
            {previewState.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </DisclosurePanel>
      ) : null}
      {ready && sendStatus ? (
        <div className="eval-pack-guidance-block runtime-handoff-destination">
          <div className="workflow-mode-header">
            <div>
              <span className="summary-card-label">Evaluation destination</span>
              <strong>
                {destination
                  ? `${destination.ase_workspace_name || destination.ase_workspace_id} · ${destination.ase_system_display_name || destination.ase_system_id}`
                  : 'Destination not confirmed'}
              </strong>
              <p>
                {destination
                  ? 'Confirm this Automated-System-Eval workspace and system before sending coverage for review.'
                  : 'A tenant admin needs to confirm the Automated-System-Eval workspace and system mapping first.'}
              </p>
            </div>
            <span className={sendSucceeded ? 'chip chip-success' : sendFailed ? 'chip chip-warning' : 'chip'}>
              {sendStatusLabel(latestSend?.status)}
            </span>
          </div>
          {latestSend?.external_url ? (
            <a className="secondary-button action-link-button runtime-handoff-external-link" href={latestSend.external_url} target="_blank" rel="noreferrer">
              Open in Automated-System-Eval
            </a>
          ) : null}
          <RuntimeRiskCoverageStatusPanel coverage={displayedCoverage} />
          {sendFailed && latestSend.failure_reason ? <p className="overview-note">{latestSend.failure_reason}</p> : null}
          {sendStatus.blockers?.length ? (
            <ul className="eval-pack-guidance-list">
              {sendStatus.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          ) : null}
          {canSend || sendFailed ? (
            <ActionCluster className="targeted-validation-actions">
              <button className="primary-button" type="button" onClick={onSend} disabled={sending || !sendStatus.can_send}>
                {sending ? 'Sending…' : sendFailed ? 'Retry send' : 'Send to evaluation coverage'}
              </button>
            </ActionCluster>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <ActionCluster className="targeted-validation-actions">
          <button className="secondary-button" type="button" onClick={onRefresh} disabled={loading}>
            {loading ? 'Retrying…' : 'Retry preview'}
          </button>
        </ActionCluster>
      ) : null}
    </div>
  );
}

export function WorkflowRuntimeScenarioCandidateCard({ candidate, selectedSystem }) {
  if (!candidate || candidate.status !== 'available') {
    return null;
  }
  return (
    <section className="detail-section runtime-scenario-panel">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Risk evaluation</p>
          <h3>Runtime-derived scenario candidate</h3>
          <p className="panel-copy">This workflow candidate comes from the selected suspicious trace pack and needs source-finding review before validation.</p>
        </div>
      </div>
      <article className="detail-card">
        <div className="workflow-mode-header">
          <div>
            <strong>{candidate.scenario_label}</strong>
            <p>{candidate.suspected_risk}</p>
          </div>
          <span className="chip chip-emphasis">{humanize(candidate.validation_mode_recommendation)}</span>
        </div>
        <div className="chip-row">
          <span className="chip">{humanize(candidate.risk_theme)}</span>
          <span className="chip">{candidate.trace_refs?.length || 0} trace refs</span>
          {candidate.label_requires_review ? <span className="chip chip-warning">Label review needed</span> : null}
        </div>
        <div className="form-actions runtime-scenario-actions">
          <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings/${candidate.source_finding_id}`}>
            Review source finding
          </a>
        </div>
      </article>
    </section>
  );
}

export function RuntimeScenarioCandidateCard({
  candidate,
  selectedSystem,
  targetedValidation,
  embedded = false,
  sectionId = 'targeted-validation-section',
}) {
  const latestReview = candidate?.latest_review || null;
  const [review, setReview] = useState(latestReview);
  const [status, setStatus] = useState(latestReview?.status || 'accepted');
  const [label, setLabel] = useState(latestReview?.reviewed_scenario_label || candidate?.scenario_label || '');
  const [expectedBehavior, setExpectedBehavior] = useState(latestReview?.expected_behavior_summary || defaultExpectedBehavior(candidate));
  const [validationDecision, setValidationDecision] = useState(latestReview?.validation_decision || candidate?.validation_mode_recommendation || 'human_review_required');
  const [evidenceGapNotes, setEvidenceGapNotes] = useState((latestReview?.evidence_gap_notes || candidate?.evidence_gaps || []).join('\n'));
  const [rejectionReason, setRejectionReason] = useState(latestReview?.rejection_reason || '');
  const [saving, setSaving] = useState(false);
  const [creatingPack, setCreatingPack] = useState(false);
  const [handoffPreview, setHandoffPreview] = useState(null);
  const [handoffPreviewLoading, setHandoffPreviewLoading] = useState(false);
  const [handoffPreviewError, setHandoffPreviewError] = useState('');
  const [sendStatus, setSendStatus] = useState(null);
  const [sendStatusLoading, setSendStatusLoading] = useState(false);
  const [sendStatusError, setSendStatusError] = useState('');
  const [sendingEvaluationCoverage, setSendingEvaluationCoverage] = useState(false);
  const [error, setError] = useState('');
  const successfulSendRef = useRef(null);

  useEffect(() => {
    const nextReview = candidate?.latest_review || null;
    setReview(nextReview);
    setStatus(nextReview?.status || 'accepted');
    setLabel(nextReview?.reviewed_scenario_label || candidate?.scenario_label || '');
    setExpectedBehavior(nextReview?.expected_behavior_summary || defaultExpectedBehavior(candidate));
    setValidationDecision(nextReview?.validation_decision || candidate?.validation_mode_recommendation || 'human_review_required');
    setEvidenceGapNotes((nextReview?.evidence_gap_notes || candidate?.evidence_gaps || []).join('\n'));
    setRejectionReason(nextReview?.rejection_reason || '');
    setHandoffPreview(null);
    setHandoffPreviewError('');
    setSendStatus(null);
    setSendStatusError('');
    successfulSendRef.current = null;
    setError('');
  }, [candidate]);

  const loadHandoffPreview = useCallback(
    async (reviewId = review?.id) => {
      if (!reviewId) {
        return;
      }
      setHandoffPreviewLoading(true);
      setHandoffPreviewError('');
      try {
        const previewPayload = await fetchRuntimeRiskSuggestionPreview(reviewId);
        setHandoffPreview(previewPayload);
      } catch (requestError) {
        setHandoffPreview(null);
        setHandoffPreviewError(requestError.message);
      } finally {
        setHandoffPreviewLoading(false);
      }
    },
    [review?.id]
  );

  useEffect(() => {
    if (review?.id && review.status === 'accepted') {
      loadHandoffPreview(review.id);
    } else {
      setHandoffPreview(null);
      setHandoffPreviewError('');
    }
  }, [loadHandoffPreview, review?.id, review?.status]);

  const loadSendStatus = useCallback(
    async (reviewId = review?.id, successfulSend = null) => {
      if (!reviewId) {
        return;
      }
      setSendStatusLoading(true);
      setSendStatusError('');
      try {
        const statusPayload = await fetchRuntimeRiskSuggestionSendStatus(reviewId);
        setSendStatus((currentStatus) => {
          const successfulStatus = successfulSend || successfulSendRef.current;
          const statusWithSuccessfulSend = successfulStatus
            ? {
                ...(currentStatus || {}),
                latest_send: successfulStatus,
                can_send: false,
                coverage_status: sentCoverageStatusFromSend(successfulStatus, currentStatus?.coverage_status),
              }
            : currentStatus;
          return mergeSuccessfulSendStatus(statusWithSuccessfulSend, statusPayload);
        });
      } catch (requestError) {
        const successfulStatus = successfulSend || successfulSendRef.current;
        if (successfulStatus) {
          setSendStatus((currentStatus) => ({
            ...(currentStatus || {}),
            latest_send: successfulStatus,
            can_send: false,
            coverage_status: sentCoverageStatusFromSend(successfulStatus, currentStatus?.coverage_status),
          }));
        } else {
          setSendStatus(null);
        }
        setSendStatusError(requestError.message);
      } finally {
        setSendStatusLoading(false);
      }
    },
    [review?.id]
  );

  useEffect(() => {
    if (review?.id && review.status === 'accepted') {
      loadSendStatus(review.id);
    } else {
      setSendStatus(null);
      setSendStatusError('');
    }
  }, [loadSendStatus, review?.id, review?.status]);

  if (!candidate) {
    return null;
  }

  if (candidate.status !== 'available') {
    return (
      <section className={['detail-section runtime-scenario-panel', embedded ? 'runtime-scenario-panel-embedded' : ''].filter(Boolean).join(' ')}>
        <div className="chapter-header">
          <div>
            <p className="chapter-kicker">Risk evaluation</p>
            <h3>Runtime-derived scenario candidate</h3>
            <p className="panel-copy">{candidate.summary}</p>
          </div>
        </div>
      </section>
    );
  }

  const reviewLinkedPackId = review?.linked_eval_pack_id;
  const activePackId = reviewLinkedPackId || targetedValidation?.active_pack_id || '';
  const activePackLabel = reviewLinkedPackId ? 'Validation pack created' : 'Validation pack available';
  const localEvalPackAvailable = candidate.local_eval_pack_available !== false;
  const localEvalPackUnavailableReason =
    candidate.local_eval_pack_unavailable_reason ||
    'External evaluation coverage is available for this risk, but no local validation pack template is available yet.';
  const observedPatternTitle = candidate.observed_runtime_pattern || candidate.scenario_label || 'Runtime pattern needs review';
  const showsSeparateAseRiskDraft = candidate.scenario_label && candidate.scenario_label !== observedPatternTitle;
  const canCreatePack = review?.status === 'accepted' && !activePackId && localEvalPackAvailable;
  const savedEvidenceGapNotes = normalizeMultiline((review?.evidence_gap_notes || []).join('\n'));
  const currentEvidenceGapNotes = normalizeMultiline(evidenceGapNotes);
  const hasSavedReview = Boolean(review?.id);
  const reviewDirty =
    !hasSavedReview ||
    status !== (review?.status || 'accepted') ||
    (label.trim() || candidate.scenario_label || '') !== (review?.reviewed_scenario_label || candidate.scenario_label || '') ||
    (expectedBehavior.trim() || '') !== (review?.expected_behavior_summary || '') ||
    (validationDecision || '') !== (review?.validation_decision || '') ||
    currentEvidenceGapNotes !== savedEvidenceGapNotes ||
    (status === 'rejected' && (rejectionReason.trim() || '') !== (review?.rejection_reason || ''));
  const saveDisabled = saving || !reviewDirty || (status === 'rejected' && !rejectionReason.trim());
  const saveLabel = saving ? 'Saving…' : reviewDirty ? 'Save scenario review' : 'Review saved';
  const latestSend = sendStatus?.latest_send;
  const sendSucceeded = latestSend && ['sent', 'duplicate_accepted'].includes(latestSend.status);
  const handoffReady = handoffPreview?.readiness_status === 'ready';
  const scenarioHref = `#/systems/${selectedSystem.id}/findings/${candidate.source_finding_id}?section=targeted-validation-section`;
  const traceReviewHref = `#/systems/${selectedSystem.id}/findings/${candidate.source_finding_id}?section=trace-review-section`;
  const needsMoreEvidence = review?.status === 'needs_more_evidence';
  const reviewRejected = review?.status === 'rejected';
  const reviewStepOpen = !hasSavedReview || reviewDirty || review?.status !== 'accepted';
  const showSaveAction = !hasSavedReview || reviewDirty;
  const showActionCluster = showSaveAction || Boolean(activePackId) || canCreatePack || review?.status === 'accepted';
  const reviewSummaryMeta = !hasSavedReview
    ? 'Next step'
    : reviewDirty
      ? 'Unsaved changes'
      : statusLabel(review?.status);
  const scenarioSteps = [
    {
      key: 'observed',
      kicker: 'Evidence',
      label: 'Observed pattern',
      meta: `${candidate.trace_refs?.length || 0} trace refs`,
      href: scenarioHref,
      status: 'complete',
      statusLabel: 'Observed',
    },
    {
      key: 'review',
      kicker: 'Triage',
      label: 'Scenario review',
      meta: statusLabel(review?.status),
      href: scenarioHref,
      status: needsMoreEvidence ? 'attention' : reviewRejected ? 'blocked' : hasSavedReview ? 'complete' : 'current',
      statusLabel: needsMoreEvidence ? 'Evidence' : reviewRejected ? 'Rejected' : hasSavedReview ? 'Saved' : 'Review',
      current: !hasSavedReview || needsMoreEvidence || reviewRejected,
    },
    {
      key: 'handoff',
      kicker: 'Coverage',
      label: 'Evaluation handoff',
      meta: needsMoreEvidence ? 'Waiting on evidence' : handoffReady ? 'Payload ready' : 'Readiness check',
      href: scenarioHref,
      status: sendSucceeded ? 'complete' : handoffReady ? 'ready' : review?.status === 'accepted' ? 'current' : 'pending',
      statusLabel: needsMoreEvidence ? 'Paused' : sendSucceeded ? 'Sent' : handoffReady ? 'Ready' : 'Prepare',
      current: review?.status === 'accepted' && !sendSucceeded,
    },
    {
      key: 'outcome',
      kicker: 'Proof',
      label: 'Evaluation outcome',
      meta: sendStatus?.coverage_status?.label || 'Await imported summary',
      href: `#/systems/${selectedSystem.id}/runtime-coverage`,
      status: sendStatus?.coverage_status?.status === 'failing_eval' ? 'attention' : sendSucceeded ? 'sent' : 'pending',
      statusLabel: sendSucceeded ? 'Track' : 'Later',
    },
  ];

  const saveReview = async () => {
    if (saveDisabled) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        status,
        reviewed_scenario_label: label.trim() || candidate.scenario_label,
        expected_behavior_summary: expectedBehavior.trim() || null,
        validation_decision: validationDecision,
        evidence_gap_notes: evidenceGapNotes
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
        rejection_reason: status === 'rejected' ? rejectionReason.trim() : null,
      };
      const saved = await createRuntimeScenarioReview(candidate.source_finding_id, payload);
      setReview(saved);
      setStatus(saved.status);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const createPack = async () => {
    if (!review?.id) {
      return;
    }
    setCreatingPack(true);
    setError('');
    try {
      const payload = await createRuntimeScenarioEvalPack(review.id);
      const packId = payload?.eval_pack?.id;
      setReview(payload.review);
      if (packId) {
        window.location.hash = `#/systems/${selectedSystem.id}/eval-packs/${packId}`;
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCreatingPack(false);
    }
  };

  const sendEvaluationCoverage = async () => {
    if (!review?.id || sendingEvaluationCoverage) {
      return;
    }
    setSendingEvaluationCoverage(true);
    setSendStatusError('');
    try {
      const sendPayload = await sendRuntimeRiskSuggestion(review.id);
      if (['sent', 'duplicate_accepted'].includes(sendPayload.status)) {
        successfulSendRef.current = sendPayload;
      }
      const successfulSendStatus = {
        latest_send: sendPayload,
        can_send: !['sent', 'duplicate_accepted'].includes(sendPayload.status),
      };
      setSendStatus((currentStatus) => ({
        ...(currentStatus || {}),
        ...successfulSendStatus,
        coverage_status: sentCoverageStatusFromSend(sendPayload, currentStatus?.coverage_status),
      }));
      try {
        const statusPayload = await fetchRuntimeRiskSuggestionSendStatus(review.id);
        setSendStatus((currentStatus) =>
          mergeSuccessfulSendStatus(
            {
              ...(currentStatus || {}),
              ...successfulSendStatus,
              coverage_status: sentCoverageStatusFromSend(sendPayload, currentStatus?.coverage_status),
            },
            statusPayload
          )
        );
      } catch (statusRequestError) {
        setSendStatusError(statusRequestError.message);
      }
    } catch (requestError) {
      setSendStatusError(requestError.message);
    } finally {
      setSendingEvaluationCoverage(false);
    }
  };

  return (
    <section
      className={['detail-section runtime-scenario-panel', embedded ? 'runtime-scenario-panel-embedded' : ''].filter(Boolean).join(' ')}
      id={sectionId}
    >
      <SectionHeader
        kicker="Scenario review"
        title="Runtime-derived scenario candidate"
        copy={candidate.summary}
      />
      <article className="detail-card runtime-scenario-card">
        <PipelineStrip
          className="runtime-scenario-pipeline"
          title="Scenario branch from triage"
          summary="Review the runtime pattern before sending compact coverage context to Automated-System-Eval."
          steps={scenarioSteps}
        />
        <div className="workflow-mode-header">
          <div>
            <span className="summary-card-label">Observed pattern</span>
            <strong>{observedPatternTitle}</strong>
            {showsSeparateAseRiskDraft ? (
              <p className="runtime-risk-profile-note">ASE risk draft: {candidate.scenario_label}</p>
            ) : null}
          </div>
          <span className={['runtime-review-status-chip', `runtime-review-status-${review?.status || 'review_needed'}`].join(' ')}>
            {statusLabel(review?.status)}
          </span>
        </div>
        <dl className="runtime-observed-facts">
          <div>
            <dt>Risk theme</dt>
            <dd>{humanize(candidate.risk_theme)}</dd>
          </div>
          <div>
            <dt>Validation</dt>
            <dd>{humanize(candidate.validation_mode_recommendation)}</dd>
          </div>
          <div>
            <dt>Trace refs</dt>
            <dd>{candidate.trace_refs?.length || 0}</dd>
          </div>
        </dl>
        <DisclosurePanel
          className="runtime-observed-details"
          title="Observed details"
          summaryMeta={candidate.common_factors?.length ? `${candidate.common_factors.length} factors` : 'Evidence note'}
        >
          <p>{candidate.suspected_risk}</p>
          <p className="overview-note">{candidate.proposed_eval_coverage}</p>
          {candidate.common_factors?.length ? (
            <div className="chip-row">
              {candidate.common_factors.slice(0, 5).map((factor) => (
                <span className="chip" key={`${factor.factor_type}-${factor.label}`}>
                  {factor.factor_type}: {factor.label}
                </span>
              ))}
            </div>
          ) : null}
        </DisclosurePanel>
        <DisclosurePanel
          className="runtime-scenario-review-disclosure"
          title="Review decision"
          summaryMeta={reviewSummaryMeta}
          defaultOpen={reviewStepOpen}
        >
          <div className={['stack', 'compact-stack', 'runtime-scenario-review-form', reviewDirty ? '' : 'runtime-scenario-review-form-saved'].filter(Boolean).join(' ')}>
            <label className="form-field">
              <span>Review decision</span>
              <select className="text-input" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="accepted">Accept as validation candidate</option>
                <option value="needs_more_evidence">Needs more evidence</option>
                <option value="rejected">Reject as not meaningful</option>
              </select>
            </label>
            <label className="form-field">
              <span>Scenario label</span>
              <input className="text-input" value={label} onChange={(event) => setLabel(event.target.value)} />
            </label>
            <label className="form-field runtime-form-span-2">
              <span>Expected behavior</span>
              <textarea className="text-input" rows="2" value={expectedBehavior} onChange={(event) => setExpectedBehavior(event.target.value)} />
            </label>
            <label className="form-field">
              <span>Validation path</span>
              <select className="text-input" value={validationDecision} onChange={(event) => setValidationDecision(event.target.value)}>
                <option value="auto_checkable">Auto-checkable</option>
                <option value="human_review_required">Human review required</option>
                <option value="external_runner_required">External evaluation required</option>
              </select>
            </label>
            <DisclosurePanel
              className="runtime-form-disclosure runtime-form-span-2"
              title="Evidence gaps"
              summaryMeta={currentEvidenceGapNotes ? 'Review notes' : 'None'}
              defaultOpen={Boolean(currentEvidenceGapNotes)}
            >
              <label className="form-field">
                <span>Notes</span>
                <textarea className="text-input" rows="2" value={evidenceGapNotes} onChange={(event) => setEvidenceGapNotes(event.target.value)} />
              </label>
            </DisclosurePanel>
            {status === 'rejected' ? (
              <label className="form-field runtime-form-span-2">
                <span>Rejection reason</span>
                <textarea className="text-input" rows="2" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} />
              </label>
            ) : null}
          </div>
        </DisclosurePanel>
        {error ? <p className="overview-note">{error}</p> : null}
        {needsMoreEvidence ? (
          <div className="runtime-evidence-next-step" role="status">
            <div>
              <span className="summary-card-label">Next step</span>
              <strong>Review trace evidence before handoff</strong>
              <p>
                Inspect suspicious and near-miss traces, capture the gap, then update the review decision when the pattern is strong enough to accept or reject.
              </p>
            </div>
            <ActionCluster align="end">
              <a className="primary-button action-link-button" href={traceReviewHref}>
                Open trace review
              </a>
              <a className="secondary-button action-link-button" href={scenarioHref}>
                Update decision
              </a>
            </ActionCluster>
          </div>
        ) : null}
        {review?.status === 'accepted' ? (
          <RuntimeRiskSuggestionPreviewPanel
            previewState={handoffPreview}
            loading={handoffPreviewLoading}
            error={handoffPreviewError}
            onRefresh={() => loadHandoffPreview(review.id)}
            sendStatus={sendStatus}
            sendStatusLoading={sendStatusLoading}
            sendStatusError={sendStatusError}
            sending={sendingEvaluationCoverage}
            onSend={sendEvaluationCoverage}
          />
        ) : null}
        {showActionCluster ? (
          <ActionCluster>
            {showSaveAction ? (
              <button className="primary-button" type="button" disabled={saveDisabled} onClick={saveReview}>
                {saveLabel}
              </button>
            ) : null}
            {activePackId ? (
              <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/eval-packs/${activePackId}`}>
                Open validation pack
              </a>
            ) : null}
            {canCreatePack ? (
              <button className="secondary-button" type="button" disabled={creatingPack} onClick={createPack}>
                {creatingPack ? 'Creating…' : 'Create new validation pack'}
              </button>
            ) : null}
            {review?.status === 'accepted' ? (
              <span
                className={
                  activePackId
                    ? 'runtime-validation-pack-note runtime-validation-pack-note-ready'
                    : localEvalPackAvailable
                      ? 'runtime-validation-pack-note'
                      : 'runtime-validation-pack-note runtime-validation-pack-note-unavailable'
                }
              >
                {activePackId ? activePackLabel : localEvalPackAvailable ? 'No validation pack yet' : localEvalPackUnavailableReason}
              </span>
            ) : null}
          </ActionCluster>
        ) : null}
      </article>
    </section>
  );
}
